/**
 * Fetches the current/most relevant Red Sox data, standings, and the active roster.
 * Always fetches from yesterday to 45 days ahead to catch today's games reliably.
 */
export const fetchRedSoxData = async () => {
  const today = new Date();
  const year = today.getFullYear();

  // Start from 3 days ago to avoid timezone edge cases missing today's game
  const pastDate = new Date(today);
  pastDate.setDate(today.getDate() - 3);

  const endRange = new Date(today);
  endRange.setDate(today.getDate() + 45);

  // Force local date parsing to Eastern Time (America/New_York) to match MLB's official dates
  const getEstDateString = (date) => {
    try {
      const options = { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric' };
      const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
      const y = parts.find(p => p.type === 'year').value;
      const m = parts.find(p => p.type === 'month').value.padStart(2, '0');
      const d = parts.find(p => p.type === 'day').value.padStart(2, '0');
      return `${y}-${m}-${d}`;
    } catch (e) {
      // Fallback if Intl fails
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  };

  const todayStr = getEstDateString(today);
  const startStr = getEstDateString(pastDate);
  const endStr = getEstDateString(endRange);

  const scheduleUrl = `https://statsapi.mlb.com/api/v1/schedule?sportId=1&teamId=111&startDate=${startStr}&endDate=${endStr}&hydrate=team,linescore,decisions`;
  const standingsUrl = `https://statsapi.mlb.com/api/v1/standings?leagueId=103&season=${year}&standingsTypes=regularSeason`;
  const rosterUrl = `https://statsapi.mlb.com/api/v1/teams/111/roster`;

  try {
    const [scheduleRes, standingsRes, rosterRes] = await Promise.all([
      fetch(scheduleUrl),
      fetch(standingsUrl),
      fetch(rosterUrl),
    ]);

    if (!scheduleRes.ok || !standingsRes.ok || !rosterRes.ok)
      throw new Error('Network response was not ok');

    const scheduleData = await scheduleRes.json();
    const standingsData = await standingsRes.json();
    const rosterData = await rosterRes.json();

    // 1. Process Standings
    const alEast = standingsData.records.find((r) => r.division.id === 201);
    const standings = alEast ? alEast.teamRecords : [];

    // 2. Process Roster
    const roster = rosterData.roster || [];

    // 3. Process Schedule
    if (!scheduleData.dates || scheduleData.dates.length === 0) {
      return { currentGame: null, schedule: [], standings, roster };
    }

    // Attach the grouping date to each game as officialDate to fix timezone leaps
    const allGames = scheduleData.dates.flatMap((d) =>
      d.games.map((g) => ({ ...g, officialDate: g.officialDate || d.date }))
    );

    // 4. Determine the "Current" Game — priority order:
    //    a) Any LIVE game right now
    //    b) Any game TODAY (Preview, Live, or Final)
    //    c) The NEXT upcoming game
    //    d) The MOST RECENT final game

    const liveGame = allGames.find((g) => g.status?.abstractGameState === 'Live');

    let currentGame = null;

    if (liveGame) {
      currentGame = liveGame;
    } else {
      // Find today's grouping
      const todayDateGroup = scheduleData.dates.find(d => d.date === todayStr);
      const todayGames = todayDateGroup ? todayDateGroup.games : [];

      if (todayGames.length > 0) {
        // Prefer a game that hasn't finished yet (Preview, Pre-Game, Warmup, etc.)
        currentGame = todayGames.find((g) => g.status?.abstractGameState !== 'Final') || todayGames[todayGames.length - 1];
      } else {
        // No games today. Look for the next upcoming game
        const nextGame = allGames.find((g) => {
          const state = g.status?.abstractGameState;
          return state === 'Preview' || state === 'Scheduled' || state === 'Pre-Game' || state === 'Warmup';
        });

        if (nextGame) {
          currentGame = nextGame;
        } else {
          // Otherwise, most recent final game
          const finalGames = allGames.filter((g) => g.status?.abstractGameState === 'Final');
          currentGame = finalGames[finalGames.length - 1] || allGames[0];
        }
      }
    }

    // 5. Hydrate Boxscore for Live/Final games
    if (
      currentGame &&
      (currentGame.status?.abstractGameState === 'Live' ||
        currentGame.status?.abstractGameState === 'Final')
    ) {
      try {
        const boxScoreRes = await fetch(
          `https://statsapi.mlb.com/api/v1/game/${currentGame.gamePk}/boxscore`
        );
        if (boxScoreRes.ok) currentGame.boxscore = await boxScoreRes.json();
      } catch (err) {
        console.error('Boxscore fetch failed', err);
      }
    }

    return { currentGame, schedule: allGames, standings, roster };
  } catch (error) {
    console.error('Data fetch error:', error);
    throw error;
  }
};